import {
  StyleSheet,
  Text,
  View,
  Image,
  FlatList,
  TouchableOpacity,
  Pressable,
} from "react-native";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../../../utility/AppColors";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { AppImages } from "../../../../utility/AppImages";
import OnGoingGameShowGeneral from "./OnGoingEventGeneral";
import OnGoingGameShowParticipant from "./OnGoingEventParticipant";
import OnGoingGameShowChat from "./OnGoingEventsChat";
import InviteSocialMediaModal from "../../Event/InviteSocialMediaModal";
import { AppScreens } from "../../../Utility/AppScreens";
import { ACTION_STATUS } from "../../../../utility/notification/notificationConfig";
import { useNotifications } from "../../../../context/NotificationContext";
import { ShowLoader } from "../../../../common/ShowLoader";
import { EVENT_STATUS } from "../../../../utility/LiveStreamSocket/LiveStreamConfig";
import CancelEventPopup from "./CancelEventPopup";
import StreamSetupPopup from "../../../../Components/settings-common-components/StreamSetupPopup";
import OnGoingGameShowStreaming from "./OnGoingEventsStreaming";
import Skeletons from "../../../../Components/skeleton-components/Skeletons";
import Header from "../Common/Header";
import { ScreenBGGradientView } from "../../../../common/CommonView";

import { useEventData } from "./hooks/useEventData";
import { useEventTimer } from "./hooks/useEventTimer";
import { OnGoingEventsRoute } from "./types/event.types";
import {
  getFormattedDate,
  getFormattedTime,
  getCheckInFormattedTime,
  hasUserRole,
} from "./utils/eventUtils";
import { useWsProvider } from "../../../../context/NativeApplicationContext";
import { toast } from "react-toastify";
import { useLiveStream } from "../../../../context/LiveStreamContext";
import {
  fetchPaymentBeltExchange,
  fetchUserBalance,
} from "../../../../services/profileAPI";
import { BELT_MEDAL_TYPES } from "../../../../config/notificationConfig";
import { getNotificationFromEvent } from "../../../../services/eventsAPI";

interface OnGoingEventsProps {
  route: OnGoingEventsRoute;
}

export default function OnGoingEvents({ route }: OnGoingEventsProps) {
  const { eventID, tab } = route.params;

  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();
  const { eventStatus } = useNotifications();
  const { isNativeAppConnected } = useWsProvider();

  const {
    details,
    loading,
    error,
    updateLoading,
    userData,
    updateEventDetails,
    cancelEventAction,
    refreshEvent,
    silentRefreshEvent,
    isOrganiser,
    activities,
  } = useEventData(eventID);

  const { isLessThanOneHour } = useEventTimer(details?.eventStartTime);

  const [isShare, setIsShare] = useState(false);
  const [selectedButton, setSelectedButton] = useState("General");
  const [cancelEventPopup, setCancelEventPopup] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [hasCheckedIn, setHasCheckedIn] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [sponsor, setSponsor] = useState("");
  const [description, setDescription] = useState("");
  const [organiser, setOrganiser] = useState(50);
  const [challenger, setChallenger] = useState(50);
  const [notificationData, setNotificationData] = useState<any>(null);

  const [hasShownInitialLoad, setHasShownInitialLoad] = useState(false);
  const [connectionReady, setConnectionReady] = useState(false);
  const [showTabScrollIndicatorRight, setShowTabScrollIndicatorRight] = useState(true);
  const [showTabScrollIndicatorLeft, setShowTabScrollIndicatorLeft] = useState(false);

  // Belt-related state
  const [arrBeltMedalType, setArrBeltMedalType] = useState(() => {
    return BELT_MEDAL_TYPES.map((belt, index) => ({
      ...belt,
      isAvailableAmountSelected: belt.id === 0 ? 1 : 0,
      isSelected: belt.id === 1, // Bronze selected by default
      currentPrize: 0,
      sellPrice: 0,
      purchaseToken: 0,
      purchasedBeltAmount: 0,
      stakeTokenAmount: 0,
      stakeBeltAmount: 0,
      stakeCurrencyAmount: 0,
      // Get responsive dimensions
      width:
        belt.dimensions?.[deviceSize]?.width ||
        belt.dimensions?.default?.width ||
        (belt.id === 0 ? 77 : 99),
      height:
        belt.dimensions?.[deviceSize]?.height ||
        belt.dimensions?.default?.height ||
        (belt.id === 0 ? 69 : 79),
    }));
  });
  const [userBalanceData, setUserBalanceData] = useState(null);

  const hasStreamSettings = useRef(false);
  const hasConnectedToLiveStream = useRef(false);

  const {
    connectToEvent,
    disconnectFromEvent,
    loader: contextLoader,
  } = useLiveStream();

  const tabData = useMemo(
    () => [
      { id: 1, title: "General" },
      { id: 2, title: "Participant" },
      { id: 6, title: "Chat" },
      { id: 7, title: ((deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM) ? "Set-up stream" : "Streaming" },
    ],
    [deviceSize]
  );

  const userRole = useMemo(
    () => (isOrganiser ? "organiser" : "challenger"),
    [isOrganiser]
  );

  const streamingInfo = useMemo(() => {
    if (!details)
      return {
        userStreamingPlatforms: [],
        twitchSwitch: false,
        youtubeSwitch: false,
        twitchStreamCreated: false,
        youtubeStreamCreated: false,
      };

    const userStreamingPlatforms = details.streamingPlatforms?.[userRole] || [];
    return {
      userStreamingPlatforms,
      twitchSwitch: userStreamingPlatforms.includes("Twitch"),
      youtubeSwitch: userStreamingPlatforms.includes("YouTube"),
      twitchStreamCreated: isOrganiser
        ? details.organizerTwitchStream
        : details.challengerTwitchStream,
      youtubeStreamCreated: isOrganiser
        ? details.organizerYoutubeStream
        : details.challengerYoutubeStream,
    };
  }, [details, userRole, isOrganiser]);

  const eventStatusInfo = useMemo(() => {
    const isEventCanceled = details?.status === EVENT_STATUS.CANCEL;
    const isEventCompleted = details?.status === EVENT_STATUS.COMPLETED;
    const participantLengthCheck = (details?.participantIDs?.length || 0) >= 2;

    return {
      isEventCanceled,
      isEventCompleted,
      participantLengthCheck,
    };
  }, [details?.status, details?.participantIDs]);

  useEffect(() => {
    if (details) {
      setSponsor(details.sponsor || "");
      setDescription(details.description || "");
      setOrganiser(details.organiserPercentage || 50);
      setChallenger(details.challengerPercentage || 50);
    }
  }, [details]);

  useEffect(() => {
    if (tab) {
      const isMobile = (deviceSize as string) === DEVICE_SIZES.XS || (deviceSize as string) === DEVICE_SIZES.SM;
      const tabMapping: Record<string, string> = {
        Chat: "Chat",
        Participant: "Participant",
        streaming: isMobile ? "Set-up stream" : "Streaming",
      };
      setSelectedButton(tabMapping[tab] || "General");
    }
  }, [tab, deviceSize]);

  useEffect(() => {
    if (details?.checkInStatus && userData?.username) {
      const isCheckedIn = isOrganiser
        ? details.checkInStatus.organiser
        : details.checkInStatus.challenger;
      setHasCheckedIn(!!isCheckedIn);
    }
  }, [details?.checkInStatus, userData?.username, isOrganiser]);

  useEffect(() => {
    if (details && !hasShownInitialLoad) {
      setHasShownInitialLoad(true);
    }
  }, [details, hasShownInitialLoad]);

  useEffect(() => {
    const storedSettings = localStorage.getItem("streamSettings");
    hasStreamSettings.current = !!storedSettings;
  }, []);

  // Fetch notification data and check if challenger rejected the challenge
  useEffect(() => {
    const fetchNotificationData = async () => {
      if (loading || !details || !userData?.AccessToken || !eventID) {
        return;
      }

      try {
        const response = await getNotificationFromEvent(
          userData.AccessToken,
          eventID
        );

        if (response?.statusCode === 200 && response.body?.notification) {
          const notification = response.body.notification;

          // Store notification data for use in other parts of the component
          setNotificationData(notification);

          // Only check for challenger rejection if user is not organiser
          if (!isOrganiser && notification.actionStatus === "rejected") {
            toast.info("You have rejected this challenge");

            // Redirect to notifications page (Challenges tab)
            const pushAction = StackActions.push(
              AppScreens.NotificationScreen,
              {
                selectedButton: "Challenges",
              }
            );
            navigation.dispatch(pushAction);
          }
        }
      } catch (error) {
        console.error("Error fetching notification data:", error);
      }
    };

    fetchNotificationData();
  }, [
    userData?.AccessToken,
    eventID,
    isOrganiser,
    navigation,
    loading,
    details,
  ]);

  // Connect to LiveStream WebSocket for chat functionality
  useEffect(() => {
    if (!userData || !details || !eventID) {
      setConnectionReady(false);
      return;
    }

    if (hasConnectedToLiveStream.current) {
      // Already connected, just mark as ready
      setConnectionReady(true);
      return;
    }

    const initConnection = async () => {
      try {
        hasConnectedToLiveStream.current = true;
        const currentUserRole = isOrganiser ? "organiser" : "challenger";

        // Connect to the event for real-time chat
        console.log(
          "Connecting to LiveStream event:",
          eventID,
          currentUserRole
        );
        connectToEvent(
          userData.username,
          eventID,
          userData.AccessToken,
          currentUserRole
        );

        // Wait a brief moment for the connection to establish
        setTimeout(() => {
          setConnectionReady(true);
        }, 500);
      } catch (error) {
        console.error("Error connecting to LiveStream:", error);
        hasConnectedToLiveStream.current = false;
        setConnectionReady(false);
      }
    };

    initConnection();

    // Cleanup on unmount
    return () => {
      console.log("Disconnecting from LiveStream event");
      disconnectFromEvent();
      hasConnectedToLiveStream.current = false;
      setConnectionReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, details, eventID, isOrganiser]);

  const isMobileView = useMemo(
    () => (size: string) =>
      [
        DEVICE_SIZES.XS,
        DEVICE_SIZES.SM,
        DEVICE_SIZES.MD,
        DEVICE_SIZES.LG,
      ].includes(size as any),
    []
  );

  const isDesktopView = useMemo(
    () => (size: string) => size === DEVICE_SIZES.XL,
    []
  );

  useEffect(() => {
    if (details && eventStatus && details.eventID === eventStatus.eventId) {
      if (typeof refreshEvent === "function") {
        refreshEvent();
      }

      // Refetch notification data when challenge status changes
      const refetchNotificationData = async () => {
        if (!userData?.AccessToken || !eventID) return;

        try {
          const response = await getNotificationFromEvent(
            userData.AccessToken,
            eventID
          );

          if (response?.statusCode === 200 && response.body?.notification) {
            setNotificationData(response.body.notification);
          }
        } catch (error) {
          console.error("Error refetching notification data:", error);
        }
      };

      refetchNotificationData();
    }
  }, [eventStatus, details?.eventID, refreshEvent, userData?.AccessToken, eventID]);

  // Fetch belt exchange rates and user balance
  useEffect(() => {
    const fetchBeltData = async () => {
      if (!userData?.AccessToken) return;

      try {
        // Fetch belt exchange rates
        const beltRates = await fetchPaymentBeltExchange(userData.AccessToken);
        if (beltRates.statusCode === 200) {
          const beltPurchaseRates = {
            Bronze: 550,
            Silver: 5500,
            Gold: 55000,
            Platinum: 550000,
          };

          const updatedBelts = arrBeltMedalType.map((item) => {
            const updatedItem = { ...item };
            if (item.title === "Bronze") {
              updatedItem.currentPrize = beltPurchaseRates.Bronze;
              updatedItem.sellPrice = Number(beltRates.sellRates.bronze);
            } else if (item.title === "Silver") {
              updatedItem.currentPrize = beltPurchaseRates.Silver;
              updatedItem.sellPrice = Number(beltRates.sellRates.silver);
            } else if (item.title === "Gold") {
              updatedItem.currentPrize = beltPurchaseRates.Gold;
              updatedItem.sellPrice = Number(beltRates.sellRates.gold);
            } else if (item.title === "Platinum") {
              updatedItem.currentPrize = beltPurchaseRates.Platinum;
              updatedItem.sellPrice = Number(beltRates.sellRates.platinum);
            }
            return updatedItem;
          });
          setArrBeltMedalType(updatedBelts);
        }

        // Fetch user balance
        const balanceData = await fetchUserBalance(userData.AccessToken);
        const responseBody =
          typeof balanceData.body === "string"
            ? JSON.parse(balanceData.body)
            : balanceData.body;
        if (balanceData) {
          setUserBalanceData(responseBody);
          const belts = responseBody?.belts;

          const updatedBelts = arrBeltMedalType.map((item) => {
            const updatedItem = { ...item };
            if (item.title === "Bronze") {
              updatedItem.value = belts?.bronzeBelt || 0;
            } else if (item.title === "Silver") {
              updatedItem.value = belts?.silverBelt || 0;
            } else if (item.title === "Gold") {
              updatedItem.value = belts?.goldBelt || 0;
            } else if (item.title === "Platinum") {
              updatedItem.value = belts?.platinumBelt || 0;
            }
            return updatedItem;
          });
          setArrBeltMedalType(updatedBelts);
        }
      } catch (error) {
        console.error("Error fetching belt data:", error);
      }
    };

    fetchBeltData();
  }, [userData?.AccessToken]);

  // Check if user is returning from token purchase and needs to reopen belt modal
  useEffect(() => {
    const returnData = localStorage.getItem("returnToBeltModal");
    if (returnData && eventID) {
      try {
        const parsed = JSON.parse(returnData);
        // Check if this is the same event
        if (parsed.eventID === eventID) {
          // Clear the stored data
          localStorage.removeItem("returnToBeltModal");

          // Reopen the belt purchase modal
          // Note: This will be triggered when user returns from Purchase screen
          // The modal state will be handled by the child components
          toast.info("Tokens purchased successfully! You can now buy belts.");
        }
      } catch (error) {
        console.error("Error parsing return data:", error);
        localStorage.removeItem("returnToBeltModal");
      }
    }
  }, [eventID, userData?.AccessToken]);

  const handleUpdateEvent = useCallback(async () => {
    const success = await updateEventDetails({
      sponsor,
      description,
      organiserPercentage: organiser,
      challengerPercentage: challenger,
    });
    return success;
  }, [updateEventDetails, sponsor, description, organiser, challenger]);

  const handleCancelEvent = useCallback(async () => {
    if (!selectedReason) {
      toast.error("Please select a cancellation reason from the dropdown");
      return false;
    }

    if (selectedReason !== "Other" && message && message.trim() === "") {
      toast.error("Please provide a valid message or leave it blank");
      return false;
    }

    const success = await cancelEventAction(selectedReason, message);
    if (success) {
      setCancelEventPopup(false);
    }
    return success;
  }, [cancelEventAction, selectedReason, message]);

  const renderItem = useCallback(
    ({ item }: { item: { id: number; title: string } }) => {
      const { isEventCanceled, isEventCompleted } = eventStatusInfo;

      const isDisabled =
        (item.title !== "General" &&
          item.title !== "Participant" &&
          (isEventCanceled ||
            (isEventCompleted && details?.winner !== null))) ||
        updateLoading;

      // Removed isStreamTabDisabled - "Set-up stream" button is now always enabled
      // The check-in condition is now applied to "Enter Stream Room" button instead
      const finalDisabled = isDisabled;
      const isSelected = selectedButton === item.title;

      if (loading) {
        return (
          <View style={{ width: "100%" }}>
            <Skeletons
              height={38}
              width={112}
              style={{
                borderRadius: 12,
              }}
            />
          </View>
        );
      }

      // Tab button with new styling
      // Selected: border-radius: 8px, border: 0.8px solid #374151, background: linear-gradient(0deg, #0C1119, #0C1119)
      // Non-selected: transparent background
      if (isSelected) {
        return (
          <LinearGradient
            colors={["#0C1119", "#0C1119"]}
            start={[0, 1]}
            end={[0, 0]}
            style={{
              width: 112,
              height: 38,
              paddingHorizontal: 6,
              paddingVertical: 16,
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 8,
              borderWidth: 0.8,
              borderColor: "#374151",
              opacity: finalDisabled ? 0.5 : 1,
            }}
          >
            <TouchableOpacity
              onPress={() => {
                setSelectedButton(item.title);
                // Removed mobile popup for Set-up stream - now shows content directly
                setShowPopup(false);
              }}
              disabled={finalDisabled}
              style={{
                width: "100%",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_12,
                  {
                    color: AppColors.white,
                    textAlign: "center",
                  },
                ]}
              >
                {item.title}
              </Text>
            </TouchableOpacity>
          </LinearGradient>
        );
      }

      // Non-selected tab
      return (
        <TouchableOpacity
          style={{
            width: 112,
            height: 38,
            paddingHorizontal: 6,
            paddingVertical: 16,
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 12,
            opacity: finalDisabled ? 0.5 : 1,
          }}
          onPress={() => {
            setSelectedButton(item.title);
            // Removed mobile popup for Set-up stream - now shows content directly
            setShowPopup(false);
          }}
          disabled={finalDisabled}
        >
          <Text
            style={[
              AppFontStyle.BOLD_ANYBODY_12,
              {
                color: AppColors.white,
                textAlign: "center",
              },
            ]}
          >
            {item.title}
          </Text>
        </TouchableOpacity>
      );
    },
    [
      selectedButton,
      eventStatusInfo,
      hasCheckedIn,
      updateLoading,
      loading,
      deviceSize,
      details?.winner,
    ]
  );

  if (error) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ScreenBGGradientView />
        <Text
          style={[
            AppFontStyle.REGULAR_ANYBODY_16,
            { color: AppColors.white, marginBottom: 20 },
          ]}
        >
          Error loading event: {error}
        </Text>
        <TouchableOpacity
          onPress={refreshEvent}
          style={{
            padding: 10,
            backgroundColor: AppColors.appViolentColor_6D08C3,
            borderRadius: 8,
          }}
        >
          <Text
            style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white }]}
          >
            Retry
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
      }}
    >
      <ScreenBGGradientView />
      <Header
        btnSearchTapped={() => {
          navigateToScreen(navigation, AppScreens.SearchEventWebScreen);
        }}
        btnWalletTapped={() => { }}
        btnStartTutorialTapped={() => { }}
        btnMessageTapped={() => { }}
        btnNotificationTapped={() => { }}
        btnProfileTapped={() => { }}
      />

      <View style={styles.container}>
        {/* Header Section with Back, Title, Cancel Event, Edit and Share */}
        <View
          style={[
            styles.deviceflexDirection,
            {
              marginTop: 10,
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              height: deviceSize === DEVICE_SIZES.XL ? "auto" : 40,
            },
          ]}
        >
          {/* Left side: Back button and Title */}
          <View style={{ flexDirection: "row", gap: deviceSize === DEVICE_SIZES.XL ? 16 : 8, alignItems: "center" }}>
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[1, 1]}
              style={{
                width: deviceSize === DEVICE_SIZES.XL ? 44 : 40,
                height: deviceSize === DEVICE_SIZES.XL ? 44 : 40,
                padding: 6,
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                justifyContent: "center",
                alignItems: "center",
                aspectRatio: 1,
              }}
            >
              <TouchableOpacity
                onPress={() => {
                  navigation.dispatch(StackActions.pop());
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Image
                  source={AppImages.icArrowLeft}
                  style={{ height: 24, width: 24 }}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </LinearGradient>
            <Text
              style={{
                color: "#FFFFFF",
                fontSize: deviceSize === DEVICE_SIZES.XL ? 32 : 16,
                fontWeight: deviceSize === DEVICE_SIZES.XL ? "800" : "700",
                lineHeight: deviceSize === DEVICE_SIZES.XL ? 36.96 : 18.48,
              }}
            >
              Event management
            </Text>
          </View>

          {/* Right side: Cancel Event, Edit icon, Share icon */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: deviceSize === DEVICE_SIZES.XL ? 12 : 8,
            }}
          >
            {/* Cancel Event Text - Show for organiser on all devices */}
            {isOrganiser && !eventStatusInfo.isEventCompleted && !eventStatusInfo.isEventCanceled && (
              <TouchableOpacity
                onPress={() => setCancelEventPopup(true)}
                disabled={eventStatusInfo.isEventCanceled || updateLoading}
              >
                <Text
                  style={{
                    color: deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM ? "#AF0707" : "#DC2626",
                    fontSize: 12,
                    fontWeight: "700",
                    lineHeight: 13.86,
                  }}
                >
                  {deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM ? "Cancel" : "Cancel Event"}
                </Text>
              </TouchableOpacity>
            )}


            {/* Cancel Icon Button removed - only edit and share icons on mobile */}

            {/* Edit Icon Button */}
            {!eventStatusInfo.isEventCanceled &&
              details?.status !== ACTION_STATUS.completed &&
              details?.status !== ACTION_STATUS.live &&
              isOrganiser && (
                <LinearGradient
                  colors={["#1F2937", "#111827"]}
                  start={[0, 0]}
                  end={[1, 1]}
                  style={{
                    display: "flex",
                    width: 40,
                    height: 40,
                    padding: 6,
                    justifyContent: "center",
                    alignItems: "center",
                    aspectRatio: 1,
                    borderRadius: 8,
                    borderWidth: 0.8,
                    borderColor: "#374151",
                  }}
                >
                  <TouchableOpacity
                    onPress={() =>
                      navigateToScreen(
                        navigation,
                        AppScreens.UpdatePublicEventScreen,
                        {
                          eventID: eventID,
                        }
                      )
                    }
                    disabled={eventStatusInfo.isEventCanceled}
                    style={{
                      width: "100%",
                      height: "100%",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Image
                      source={AppImages.Ko_ongoing_edit}
                      style={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        aspectRatio: 1,
                      }}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </LinearGradient>
              )}

            {/* Share Icon Button */}
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[1, 1]}
              style={{
                display: "flex",
                width: 40,
                height: 40,
                padding: 6,
                justifyContent: "center",
                alignItems: "center",
                aspectRatio: 1,
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                opacity:
                  eventStatusInfo.isEventCanceled ||
                    notificationData?.actionStatus !== "accepted"
                    ? 0.5
                    : 1,
              }}
            >
              <TouchableOpacity
                onPress={() => setIsShare(true)}
                disabled={
                  eventStatusInfo.isEventCanceled ||
                  notificationData?.actionStatus !== "accepted"
                }
                style={{
                  width: "100%",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Image
                  source={AppImages.Ko_ongoing_share}
                  style={{
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                    aspectRatio: 1,
                  }}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </LinearGradient>
          </View>
        </View>

        {/* Separator Line */}
        <View
          style={{
            height: 2,
            backgroundColor: "#374151",
            marginTop: 16,
          }}
        />

        {/* Tabs Section with Gradient Background */}
        <View style={{ marginTop: 16 }}>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Tabs Container with Gradient */}
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[0, 1]}
              style={{
                ...(isMobileView(deviceSize) ? { flex: 1 } : {}),
                borderRadius: 8,
                paddingHorizontal: 8,
                paddingVertical: 8,
                height: 54,
                justifyContent: "center",
                marginRight: isDesktopView(deviceSize) ? 12 : 0,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <FlatList
                endFillColor={AppColors.white}
                overScrollMode="never"
                horizontal={true}
                showsHorizontalScrollIndicator={false}
                data={tabData}
                renderItem={renderItem}
                keyExtractor={(item) => item.id.toString()}
                contentContainerStyle={{ gap: 8, alignItems: "center" }}
                onScroll={(event) => {
                  const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
                  const isAtStart = contentOffset.x <= 10;
                  const isAtEnd = contentOffset.x + layoutMeasurement.width >= contentSize.width - 10;
                  setShowTabScrollIndicatorLeft(!isAtStart);
                  setShowTabScrollIndicatorRight(!isAtEnd);
                }}
                scrollEventThrottle={16}
              />
              {/* Left Scroll Indicator - Mobile Only (shows when scrolled right) */}
              {isMobileView(deviceSize) && showTabScrollIndicatorLeft && (
                <LinearGradient
                  colors={["#111827", "rgba(17, 24, 39, 0.9)", "transparent"]}
                  start={[0, 0]}
                  end={[1, 0]}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 35,
                    justifyContent: "center",
                    alignItems: "flex-start",
                    paddingLeft: 6,
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                  }}
                  pointerEvents="none"
                >
                  <Text style={{ color: "#9CA3AF", fontSize: 18, fontWeight: "bold" }}>‹</Text>
                </LinearGradient>
              )}
              {/* Right Scroll Indicator - Mobile Only (shows when more tabs available) */}
              {isMobileView(deviceSize) && showTabScrollIndicatorRight && (
                <LinearGradient
                  colors={["transparent", "rgba(17, 24, 39, 0.9)", "#111827"]}
                  start={[0, 0]}
                  end={[1, 0]}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: 35,
                    justifyContent: "center",
                    alignItems: "flex-end",
                    paddingRight: 6,
                    borderTopRightRadius: 8,
                    borderBottomRightRadius: 8,
                  }}
                  pointerEvents="none"
                >
                  <Text style={{ color: "#9CA3AF", fontSize: 18, fontWeight: "bold" }}>›</Text>
                </LinearGradient>
              )}
            </LinearGradient>

            {/* Time Boxes - Desktop View - Only show in General tab */}
            {isDesktopView(deviceSize) && selectedButton === "General" ? (
              <View style={{ flexDirection: "row", gap: 12 }}>
                {/* Match Time Box */}
                <View
                  style={{
                    minWidth: 200,
                    paddingVertical: 10,
                    paddingHorizontal: 12,
                    borderRadius: 12,
                    backgroundColor: "#282828",
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <Image source={AppImages.MatchTime} style={styles.icon} />
                    <Text style={styles.timeLabel}>Match Time</Text>
                  </View>
                  <Text style={[styles.timeValue, { textAlign: "left", marginTop: 4 }]}>
                    {details?.eventStartTime
                      ? getFormattedTime(details.eventStartTime)
                      : "N/A"}
                  </Text>
                  <Text style={[styles.timeNote, { textAlign: "left", marginTop: 2 }]}>
                    {details?.eventStartTime
                      ? getFormattedDate(details.eventStartTime)
                      : "N/A"}
                  </Text>
                </View>

                {/* Check-in Opens Box */}
                <View
                  style={{
                    minWidth: 200,
                    paddingVertical: 10,
                    paddingHorizontal: 12,
                    borderRadius: 12,
                    backgroundColor: "#282828",
                    borderWidth: 1,
                    borderColor: "#FACC15",
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <Image
                      source={AppImages.CheckInTime}
                      style={styles.icon}
                    />
                    <Text style={styles.timeLabel}>Check-in Opens</Text>
                  </View>
                  <Text
                    style={[
                      styles.timeValue,
                      { color: "#FACC15", textAlign: "left", marginTop: 4 },
                    ]}
                  >
                    {details?.eventStartTime
                      ? getCheckInFormattedTime(details.eventStartTime)
                      : "N/A"}
                  </Text>
                  <Text style={[styles.timeNote, { textAlign: "left", marginTop: 2 }]}>
                    90 minutes before match
                  </Text>
                </View>
              </View>
            ) : null}
          </View>
        </View>


        {selectedButton === "General" && (
          <OnGoingGameShowGeneral
            showLoader={loading && !hasShownInitialLoad}
            userData={userData}
            isOrganiserId={isOrganiser}
            details={details}
            description={description}
            sponsor={sponsor}
            setSponsor={setSponsor}
            IsEventCanceled={eventStatusInfo.isEventCanceled}
            updateShowLoader={updateLoading}
            callUpdateEventAPI={handleUpdateEvent}
            setDescription={setDescription}
            setSelectedButton={setSelectedButton}
            IsEventCompleted={eventStatusInfo.isEventCompleted}
            hasCheckedIn={hasCheckedIn}
            setHasCheckedIn={setHasCheckedIn}
            silentRefresh={silentRefreshEvent}
            setShowPopup={setShowPopup}
            twitchStreamCreated={streamingInfo.twitchStreamCreated}
            youtubeStreamCreated={streamingInfo.youtubeStreamCreated}
            isNativeAppConnected={isNativeAppConnected}
          />
        )}
        {selectedButton === "Participant" && (
          <OnGoingGameShowParticipant
            accessToken={userData?.AccessToken || ""}
            showLoader={loading}
            eventID={eventID}
            setDetails={refreshEvent}
            isOrganiserId={isOrganiser}
            details={details}
            userData={userData}
            IsEventCanceled={eventStatusInfo.isEventCanceled}
            IsEventCompleted={eventStatusInfo.isEventCompleted}
            arrBeltMedalType={arrBeltMedalType}
            setArrBeltMedalType={setArrBeltMedalType}
            userBalanceData={userBalanceData}
            activities={activities}
          />
        )}
        {selectedButton === "Chat" ? (
          details?.ChatConversationId ? (
            <OnGoingGameShowChat
              ChatConversationId={details.ChatConversationId}
              connectionReady={connectionReady}
              isLoading={contextLoader || !connectionReady}
              eventID={eventID}
              accessToken={userData?.AccessToken || ""}
              userData={userData}
              isOrganiserId={isOrganiser}
              details={details}
              refreshEvent={refreshEvent}
              IsEventCanceled={eventStatusInfo.isEventCanceled}
              IsEventCompleted={eventStatusInfo.isEventCompleted}
              arrBeltMedalType={arrBeltMedalType}
              setArrBeltMedalType={setArrBeltMedalType}
              userBalanceData={userBalanceData}
            />
          ) : (
            <View style={styles.container}>
              <Text
                style={[
                  deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                    ? AppFontStyle.BOLD_ANYBODY_24
                    : AppFontStyle.BOLD_ANYBODY_20,
                  {
                    alignSelf: "center",
                    textAlign: "center",
                    color: AppColors.white,
                  },
                ]}
              >
                Opponent Still Not Accepted the Challenge
              </Text>
            </View>
          )
        ) : null}
        {/* Stream setup - now available on all devices including mobile */}
        {(selectedButton === "Set-up stream" || selectedButton === "Streaming") ? (
          <OnGoingGameShowStreaming
            eventDetails={details}
            accessToken={userData?.AccessToken || ""}
            username={userData?.username || ""}
            IsEventCanceled={eventStatusInfo.isEventCanceled}
            setDetails={refreshEvent}
            multiStreamEnabled={details?.multiStreamEnabled?.[userRole]}
            isLessThanOneHour={isLessThanOneHour}
            participantLengthCheck={eventStatusInfo.participantLengthCheck}
            hasCheckedIn={hasCheckedIn}
            twitchSwitch={streamingInfo.twitchSwitch}
            userTwitchStreamConnected={details?.userTwitchStreamConnected}
            twitchStreamCreated={streamingInfo.twitchStreamCreated}
            youtubeSwitch={streamingInfo.youtubeSwitch}
            userYoutubeStreamConnected={details?.userYoutubeStreamConnected}
            youtubeStreamCreated={streamingInfo.youtubeStreamCreated}
            userData={userData}
            isOrganiser={isOrganiser}
          />
        ) : null}
        {isShare && (
          <InviteSocialMediaModal
            accessToken={userData?.AccessToken || ""}
            challengeDetails={{}}
            showModal={isShare}
            setShowModal={setIsShare}
            eventType={"OnGoingEvents"}
            eventID={eventID}
            status={details?.status}
          />
        )}
        {cancelEventPopup && (
          <CancelEventPopup
            showModal={cancelEventPopup}
            setShowModal={setCancelEventPopup}
            selectedReason={selectedReason}
            setSelectedReason={setSelectedReason}
            customReason={customReason}
            setCustomReason={setCustomReason}
            message={message}
            setMessage={setMessage}
            handleCancelEvent={handleCancelEvent}
            isCancelling={updateLoading}
            accessToken={userData?.AccessToken || ""}
            eventID={eventID}
            viewers={[
              ...(details?.participantDetails || []),
              ...(details?.attendeeIDs || []).filter(
                (id: string) => !details?.participantDetails?.find((p: any) => p.userID === id)
              ).map((id: string) => ({ userID: id }))
            ]}
            totalViewers={(details?.participantDetails?.length || 0) + (details?.attendeeIDs?.length || 0)}
          />
        )}
        <ShowLoader showIndicater={updateLoading} isBackGround={true} />
        {showPopup && (
          <StreamSetupPopup
            screen="LiveStream"
            showPopup={showPopup}
            setShowPopup={setShowPopup}
            onClose={() => {
              setShowPopup(false);
              setSelectedButton("General");
            }}
          />
        )}
      </View>
    </View>
  );
}

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      paddingHorizontal: "3.5%",
      paddingTop: "1%",
    },
    btnOuterView: {
      margin: 5,
      backgroundColor: AppColors.gray_1B1B1B,
      borderRadius: 12,
      paddingHorizontal: 10,
    },
    btnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 30,
      shadowOpacity: 0,
      height: 40,
      borderRadius: 10,
      margin: 10,
    },
    deviceflexDirection: {
      flexDirection: "row",
    },
    timeContainer: {
      borderRadius: 12,
    },
    timeBox: {
      flex: 1,
      padding: 7,
      borderRadius: 12,
      justifyContent: "flex-start",
      minHeight: 50,
    },
    matchTimeBox: {
      backgroundColor: AppColors.gray_1B1B1B,
    },
    checkInBox: {
      backgroundColor: AppColors.gray_1B1B1B,
    },
    icon: {
      width: 20,
      height: 20,
      marginBottom: 5,
    },
    timeLabel: {
      ...AppFontStyle.BOLD_ANYBODY_12,
      color: "#D1D5DB",
      textAlign: "left",
      marginBottom: 5,
    },
    timeValue: {
      ...AppFontStyle.BOLD_ANYBODY_16,
      color: AppColors.white,
      textAlign: "left",
      marginVertical: 5,
    },
    timeDate: {
      ...AppFontStyle.REGULAR_ANYBODY_12,
      color: "#9CA3AF",
      textAlign: "left",
    },
    timeNote: {
      ...AppFontStyle.REGULAR_ANYBODY_10,
      color: "#9CA3AF",
      textAlign: "left",
      marginTop: 3,
      marginBottom: 5,
    },
  },
  {
    [DEVICE_SIZES.MD]: {
      container: {
        flex: 1,
        backgroundColor: "transparent",
        paddingHorizontal: "3.5%",
        paddingTop: "2.5%",
      },
      btnOuterView: {
        backgroundColor: AppColors.gray_1B1B1B,
        borderRadius: 12,
        paddingHorizontal: 5,
        width: "100%",
      },
      btnStyle: {
        paddingVertical: 0,
        paddingHorizontal: 32,
        backgroundColor: "transparent",
        shadowOpacity: 0,
        height: 40,
        borderRadius: 10,
        margin: 5,
      },
      deviceflexDirection: {
        flexDirection: "row",
      },
      timeBox: {
        padding: 12,
        width: 200,
        minHeight: 50,
      },
      icon: { width: 18, height: 18 },
      timeValue: { ...AppFontStyle.BOLD_ANYBODY_14 },
      timeLabel: { ...AppFontStyle.BOLD_ANYBODY_11 },
      timeDate: { ...AppFontStyle.REGULAR_ANYBODY_11 },
    },
    [DEVICE_SIZES.LG]: {
      container: {
        flex: 1,
        backgroundColor: "transparent",
        paddingHorizontal: "3.5%",
        paddingTop: "2.5%",
      },
      btnOuterView: {
        margin: 5,
        backgroundColor: AppColors.gray_1B1B1B,
        borderRadius: 12,
        paddingHorizontal: 10,
        width: "100%",
      },
      btnStyle: {
        paddingVertical: 0,
        paddingHorizontal: 30,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 10,
        margin: 10,
      },
      deviceflexDirection: {
        flexDirection: "row",
      },
      timeBox: {
        padding: 7,
        width: 250,
        minHeight: 50,
      },
      icon: { width: 20, height: 20 },
      timeValue: { ...AppFontStyle.BOLD_ANYBODY_16 },
      timeLabel: { ...AppFontStyle.BOLD_ANYBODY_12 },
      timeDate: { ...AppFontStyle.REGULAR_ANYBODY_12 },
      timeNote: { ...AppFontStyle.REGULAR_ANYBODY_10 },
    },
    [DEVICE_SIZES.SM]: {
      container: {
        flex: 1,
        backgroundColor: "transparent",
        paddingHorizontal: "3.5%",
      },
      btnOuterView: {
        margin: 5,
        backgroundColor: AppColors.gray_1B1B1B,
        borderRadius: 12,
        paddingHorizontal: 10,
        width: "100%",
      },
      btnStyle: {
        paddingVertical: 0,
        paddingHorizontal: 27,
        backgroundColor: "transparent",
        shadowOpacity: 0,
        height: 40,
        borderRadius: 10,
        margin: 5,
      },
      deviceflexDirection: {
        flexDirection: "row",
      },
      timeBox: {
        padding: 5,
        width: 180,
        minHeight: 50,
      },
      icon: { width: 16, height: 16 },
      timeValue: { ...AppFontStyle.BOLD_ANYBODY_12 },
      timeLabel: { ...AppFontStyle.BOLD_ANYBODY_10 },
      timeDate: { ...AppFontStyle.REGULAR_ANYBODY_10 },
      timeNote: { ...AppFontStyle.REGULAR_ANYBODY_8 },
    },
    [DEVICE_SIZES.XS]: {
      container: {
        flex: 1,
        backgroundColor: "transparent",
        paddingHorizontal: "3.5%",
      },
      btnOuterView: {
        backgroundColor: AppColors.gray_1B1B1B,
        borderRadius: 12,
        paddingHorizontal: 5,
      },
      btnStyle: {
        paddingVertical: 0,
        paddingHorizontal: 5,
        backgroundColor: "transparent",
        shadowOpacity: 0,
        height: 40,
        borderRadius: 10,
        margin: 5,
      },
      deviceflexDirection: {
        flexDirection: "row",
      },
      timeBox: {
        padding: 3,
        width: 160,
        minHeight: 50,
      },
      icon: { width: 14, height: 14 },
    },
  }
);
